import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AnimatedLogo from './AnimatedLogo';
import promoVideo from './tanitimfilmii.mp4';

// Custom Typewriter Hook
const useTypewriter = (text, speed = 100, delay = 0) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!text) return;

    const timeout = setTimeout(() => {
      let i = 0;
      const timer = setInterval(() => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1));
          i++;
        } else {
          clearInterval(timer);
          setIsComplete(true);
        }
      }, speed);

      return () => clearInterval(timer);
    }, delay);

    return () => clearTimeout(timeout);
  }, [text, speed, delay]);

  return { displayText, isComplete };
};

const Hero = () => {
  const [isLogoAnimationComplete, setIsLogoAnimationComplete] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogoAnimationComplete = () => {
    setIsLogoAnimationComplete(true);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Scroll event listener for header buttons
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typewriter animasyonları
  const mainSlogan = useTypewriter(
    isLogoAnimationComplete ? "1967'DEN BERİ YOLUN,SAĞLAM BAŞLANGICI" : "",
    120,
    800
  );

  return (
    <>
      {/* Hero Section - Inspired by Goodyear Design */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={promoVideo} type="video/mp4" />
          </video>
        </div>

        {/* Background with Mountain and Road Scene - as fallback */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-red-900 opacity-30">
        </div>

        {/* Navigation */}
        <nav className={`absolute top-6 left-6 right-6 z-30 transition-all duration-500 ${isLogoAnimationComplete ? 'opacity-100' : 'opacity-0'}`}>
          {/* Desktop Navigation */}
          <div className="hidden md:flex justify-end space-x-6">
            <Link to="/servisler" className={`transition-all duration-300 font-medium text-lg ${isScrolled ? 'text-white' : 'text-white'} hover:text-red-400`}>
              SERVİSLER
            </Link>
            <Link 
              to="/neden-biz"
              className={`transition-all duration-300 font-medium text-lg ${isScrolled ? 'text-white' : 'text-white'} hover:text-red-400`}
            >
              NEDEN BİZ
            </Link>
            <Link 
              to="/markalar"
              className={`transition-all duration-300 font-medium text-lg ${isScrolled ? 'text-white' : 'text-white'} hover:text-red-400`}
            >
              MARKALAR
            </Link>
            <Link to="/hakkimizda" className={`transition-all duration-300 font-medium text-lg ${isScrolled ? 'text-white' : 'text-white'} hover:text-red-400`}>
              HAKKIMIZDA
            </Link>
            <Link to="/iletisim" className={`transition-all duration-300 font-medium text-lg ${isScrolled ? 'text-white' : 'text-white'} hover:text-red-400`}>
              İLETİŞİM
            </Link>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex justify-end items-center">            
            {/* Hamburger Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="text-white hover:text-red-400 focus:outline-none"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-black bg-opacity-90 backdrop-blur-sm rounded-lg mt-2 py-4 px-6">
              <div className="flex flex-col space-y-4">
                <Link 
                  to="/servisler" 
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  SERVİSLER
                </Link>
                <Link 
                  to="/neden-biz"
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  NEDEN BİZ
                </Link>
                <Link 
                  to="/markalar"
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  MARKALAR
                </Link>
                <Link 
                  to="/hakkimizda" 
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  HAKKIMIZDA
                </Link>
                <Link 
                  to="/iletisim" 
                  className="text-white hover:text-red-400 transition-colors duration-300 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  İLETİŞİM
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Main Hero Content */}
        <div className="relative z-10 min-h-screen flex items-center">
          <div className="container mx-auto px-6">
            {/* Animated Logo */}
            <AnimatedLogo onAnimationComplete={handleLogoAnimationComplete} />
            
            {/* Main Slogan */}
            <div className={`transition-opacity duration-1000 ${isLogoAnimationComplete ? 'opacity-100' : 'opacity-0'}`}>
              <div className="text-center mt-16">
                <h1 className="sm:text-2xl lg:text-7xl xl:text-8xl font-black text-white tracking-wider mb-8 leading-tight px-4" style={{fontSize: window.innerWidth >= 768 ? '60px' : '34px'}}>
                  {mainSlogan.displayText}
                  {mainSlogan.displayText && !mainSlogan.isComplete && (
                  <span className="animate-pulse">|</span>
                )}
              </h1>
              
                {mainSlogan.isComplete && (
                  <div className="animate-fade-in-up" style={{animationDelay: '0.5s'}}>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero; 